import type { SxProps, Theme } from '@mui/material/styles'
import { NETWORK_HUES } from './networkHues.ts'

/** How much of the map's tiles shows over the colour of the page. 1 is the tiles as they come. */
const MAP_TILE_OPACITY = 0.4

/**
 * The highlight by network (app#498). ⚠ Last in the object, so that each color rule comes after the
 * state rule of equal weight. The network's badge is the legend: the badge and its lines take the
 * color of the arcs.
 * ⚠ A network's badge on the map holds white text in both themes, so it takes the darker value.
 */
function hueRules(mode: string) {
  const hues = mode === 'dark' ? NETWORK_HUES.dark : NETWORK_HUES.light
  return Object.fromEntries(
    hues.flatMap((color, hue) => [
      [`.arcs .hue-${hue}`, { stroke: color }],
      [`.network.point.hue-${hue}`, { fill: color }],
      [`.link.hue-${hue}`, { stroke: color, strokeWidth: 2 }],
      [`.map .link.hue-${hue}`, { stroke: color, strokeWidth: 2 }],
      [`.map .chip.network.hue-${hue}`, { fill: NETWORK_HUES.light[hue] }],
    ])
  )
}

export function getGraphStyles(theme: Pick<Theme, 'palette' | 'typography'>): SxProps<Theme> {
  const { palette, typography } = theme

  return {
    top: 0,
    position: 'absolute',
    '.account.point .avatar': { stroke: 'none' },
    '.account.point path': { fill: palette.primary.main, stroke: 'none' },
    '.account.point rect': { fill: palette.primary.light },
    '.account.point circle': { fill: palette.background.default },
    '.account.point.active': { stroke: palette.contrast.high, strokeWidth: 2 },
    // ⚠ A HIGHLIGHT FADES IN AND OUT, and `stroke-width` is half of it. The lit set moves with
    // the cursor, so an instant swap leaves the reader with two pictures in mind — the one that
    // was lit a frame ago and the one that is — and no way to tell which they are looking at.
    // Fading the old one out as the new one comes up is what says they are the same highlight
    // moving, rather than a second one appearing.
    '.link': {
      stroke: palette.contrast.minimal,
      strokeDasharray: '3,5',
      // ⚠ THE FADE IS ASYMMETRIC, AND THE SLOW HALF IS THE ONE HERE. A transition is read off the
      // state being moved TO, so this duration is the fade OUT and the one on `.active` is the
      // light UP. Lighting up fast keeps the highlight on the cursor; fading out slowly is what
      // makes the lit set read as one highlight moving rather than two appearing.
      transition: 'stroke 0.7s ease-out, stroke-width 0.7s ease-out',
    },
    '.link.streaming': { stroke: palette.stream.main },
    // The fast half of the fade — see `.link` above.
    '.link.active': { stroke: palette.contrast.high, strokeWidth: 2, transition: 'stroke 0.12s, stroke-width 0.12s' },
    '.flow-particle': { fill: palette.stream.main },
    '.flow-particle.rx': { fill: palette.success.main },
    '.flow-particle.tx': { fill: palette.error.main },
    '.particles': { pointerEvents: 'none' },
    '.particle': {
      fill: palette.stream.main,
      pointerEvents: 'none',
      filter: 'drop-shadow(0 0 2px rgba(0, 187, 216, 0.5))',
    },
    '.particle.hidden': { opacity: 0 },
    '.point.active': { stroke: palette.contrast.high, strokeWidth: 2, transition: 'stroke 0.12s, stroke-width 0.12s' },
    '.point.selected': { stroke: palette.background.default, strokeWidth: 3 },
    '.point.offline': { fill: palette.contrast.minimal },
    '.node.point': { fill: palette.node.main },
    '.node.point.active': { stroke: palette.contrast.high, strokeWidth: 2 },
    '.node.point.selected': { stroke: palette.contrast.max, strokeWidth: 2 },
    '.node.point.offline': { fill: palette.contrast.minimal },
    // The marks after a node's name (nobgp-router#2010, `marks.ts`): decoration, not hit area, so
    // the node's hover and click are unchanged. ⚠ Every colour is set on the mark itself: the
    // point group's `fill` and `stroke` (active, selected, offline) would be inherited otherwise.
    '.point .marks': { pointerEvents: 'none', stroke: 'none' },
    // The spacer the marks are drawn on: letters that keep their width and are not seen.
    '.label .marks-space': { fillOpacity: 0, strokeOpacity: 0 },
    '.point .marks .aws path, .point .marks .tailscale path': { fill: palette.text.secondary },
    // They follow the name: grey with `.label.offline` here, hidden with `.map .label.inner` below.
    '.point .marks.offline': { opacity: 0.6 },
    // A hovered offline node shows its name (`.label.active`), so its marks too.
    '.point .marks.offline.active': { opacity: 1 },
    // Another node is lit: the name is dimmed, not hidden (`.label.node.hidden`), so are the marks.
    '.point .marks.dimmed': { opacity: 0.5 },
    // ⚠ Two classes beats `.point`, so this rule owns the whole transition for a service — the
    // slow fade out included, or a service's highlight would drop four times faster than a node's.
    '.service.point': {
      fill: palette.contrast.low,
      transition: 'fill 0.3s, stroke 0.7s ease-out, stroke-width 0.7s ease-out',
    },
    // ⚠ THREE CLASSES, BECAUSE TWO IS NOT ENOUGH HERE. `.service.point` above is declared AFTER
    // `.point.active` and ties it on specificity, so it won the whole transition — the light UP
    // included, and a service lit at the fade-OUT speed while every node, name and link lit six
    // times faster. This rule outranks both. `fill` is carried over from `.service.point`, which
    // is otherwise dropped along with it.
    '.service.point.active': {
      stroke: palette.contrast.high,
      strokeWidth: 1.5,
      cursor: 'pointer',
      transition: 'fill 0.3s, stroke 0.12s, stroke-width 0.12s',
    },
    '.service.point.selected': { stroke: palette.background.default, strokeWidth: 3 },
    '.service.point.expanded': { fill: palette.secondary.main },
    // ⚠⚠ THIS IS THE HIGHLIGHT A READER SEES ON A SERVICE, so it is where the fade has to be.
    // `.service.point.expanded.active` sets the GROUP's `stroke-width` to 0, so a service's own
    // outline is invisible exactly when it is lit — what changes is the icon's stroke, here. A
    // `transition` is not inherited, so the one on the group never reached this element and the
    // service highlight snapped on and off while everything else on the canvas faded.
    '.service.point.expanded use': {
      stroke: palette.background.default,
      strokeWidth: 3,
      // The slow half — the fade out. See `.link`.
      transition: 'stroke 0.7s ease-out',
    },
    '.service.point.expanded.active': { strokeWidth: 0 },
    '.service.point.expanded.active use': { stroke: palette.contrast.max, transition: 'stroke 0.12s' },
    '.service.point.offline': { fill: palette.contrast.minimal },
    '.network.point': { fill: palette.contrast.high },
    '.network.point.active': { stroke: palette.contrast.max, strokeWidth: 2 },
    '.location.point': { fill: palette.contrast.minimal, transition: 'fill 0.4s' },
    '.location.point.streaming': { fill: palette.stream.main },
    '.location.point.active': { fill: palette.contrast.high },
    // The map view (`mapCore.ts`, nobgp-router#1934). The drawing sits on map tiles and not on the
    // page colour, and the map under it takes the pan and the zoom.
    //
    // ⚠ `pointer-events` is the mechanism. The `<svg>` itself takes none, so a drag or a wheel on
    // an empty part reaches the map; a node takes them back, or it could not be selected.
    '.map .point': { pointerEvents: 'auto', cursor: 'pointer' },
    // A halo in the page colour, so a name reads on a road, a border or a sea.
    // A name on the map stands out from the map's own names (Benoit, 2026-10-02): the colour of
    // body text, a little heavier, on a wider halo in the page colour, so it reads on a road, a
    // border or a sea.
    '.map .label': {
      // A name takes no click: a name that lies on an icon must not stop the click on the icon.
      pointerEvents: 'none',
      fill: palette.text.primary,
      fontWeight: 500,
      paintOrder: 'stroke',
      stroke: palette.background.default,
      strokeWidth: 4,
      strokeLinejoin: 'round',
    },
    // The name of a node on the map is on a chip (`placeChips` in `mapCore.ts`), with no halo
    // (Benoit, 2026-10-02: option A). The chip takes the name's classes.
    '.map .label.node, .map .label.network': { stroke: 'none', fontWeight: 400, fill: palette.text.primary, fontSize: 12 },
    // No name under the home icon (Benoit, 2026-10-03: "the user name does not have to show up near
    // the home location"). ⚠ `display`, so the name has no box and takes no room (`measure`).
    '.map .label.account': { display: 'none' },
    // ⚠ Every chip is a BADGE (`placeChips`): it holds its node's icon and its name, and a click on it
    // is a click on the node. So it takes the pointer, unlike a name.
    '.map .chip': {
      fill: palette.background.paper,
      // A little of the map shows through (Benoit, 2026-10-02: "some transparency to the chip").
      fillOpacity: 0.82,
      stroke: palette.divider,
      strokeWidth: 0.8,
      pointerEvents: 'auto',
      cursor: 'pointer',
      transition: 'fill 0.12s, stroke 0.12s',
    },
    // Lit (the reader points at it): the border in the colour of a node.
    '.map .chip.active': { stroke: palette.node.main, strokeWidth: 1.2 },
    // Selected: the chip in the colour of a node, so the name stands out.
    '.map .chip.selected': { fill: palette.node.main, fillOpacity: 1, stroke: palette.node.main },
    '.map .label.node.selected, .map .label.network.selected': { fill: palette.background.paper, fontWeight: 600 },
    // Offline: a dashed border and the quiet colour.
    '.map .chip.offline': { strokeDasharray: '3 2' },
    // A service's badge: its icon and its name in one box in the service colour, the name in white
    // (Benoit, 2026-10-03, option C: "Mac Terminal for example was hard to spot").
    '.map .chip.service': { fill: palette.secondary.main, fillOpacity: 1, stroke: 'none' },
    '.map .chip.service.active': { stroke: palette.contrast.high, strokeWidth: 1.5 },
    '.map .chip.service.selected': { fill: palette.secondary.main, stroke: palette.background.default, strokeWidth: 2.5 },
    '.map .chip.service.offline': { fill: palette.contrast.low },
    '.map .label.service': { fill: palette.common.white, stroke: 'none', fontWeight: 500 },
    // A network's badge: dark, with its icon and its name in white (Benoit, 2026-10-03: "ok with
    // the dark networks"), so it is never read as a node.
    '.map .chip.network': { fill: palette.grey[700], fillOpacity: 1, stroke: 'none' },
    '.map .chip.network.active': { stroke: palette.contrast.max, strokeWidth: 1.5 },
    '.map .chip.network.selected': { fill: palette.grey[900], stroke: palette.background.default, strokeWidth: 2 },
    '.map .label.network': { fill: palette.common.white },
    // ⚠ Four classes, to outrank the selected name of a node, which is in the paper colour: dark in
    // the dark theme, on a dark badge.
    '.map .label.network.selected': { fill: palette.common.white, fontWeight: 500 },
    '.map .network.point .net-icon rect': { fill: palette.common.white, stroke: 'none' },
    '.map .network.point .net-icon path': { fill: 'none', stroke: palette.common.white, strokeWidth: 1.3, strokeLinecap: 'round', strokeLinejoin: 'round' },
    // A service's name that is not shown takes its badge with it: the icon stays.
    '.map .chip.service.dimmed:not(.active)': { visibility: 'hidden' },
    // A name that is not shown hides its chip too.
    // ⚠ `visibility` and never `opacity`: a chip fades on its opacity ATTRIBUTE (`placeChips`).
    '.map .chip.inner:not(.active):not(.selected)': { visibility: 'hidden' },
    // ⚠ A name that is dimmed because another node is lit or selected stays READABLE on the map:
    // the graph's `contrast.low` was too light on a chip ("the text in an unselected chip ...
    // should be more readable, darker"). The secondary colour, on the same chip. ⚠ Offline names
    // too: `.label.node.hidden` dims them on the graph, and it ties with `.map .label.offline`.
    '.map .label.node.hidden': { fill: palette.text.secondary },
    // ⚠ An offline node keeps its name on the map ("offline node should still show their name"),
    // and on the graph since 2026-10-21 (`.label.offline`). Here it is in the map's quiet colour.
    '.map .label.offline': { opacity: 1, fill: palette.text.secondary },
    // The same line, heavier: `contrast.minimal` is tuned for a flat page and is lost on tiles.
    '.map .link': { stroke: palette.contrast.medium },
    '.map .link.streaming': { stroke: palette.stream.main },
    '.map .link.active': { stroke: palette.contrast.high },
    // The tiles are faded toward the colour of the page (Benoit, 2026-10-03: "this is a networking
    // software, not a road map, so the map could be a little more faded. it is here to show where
    // the nodes are"). ⚠ On the canvas only: the nodes, the credits and the buttons are not in it.
    '.maplibregl-map': { backgroundColor: palette.background.default },
    '.maplibregl-map .maplibregl-canvas': { opacity: MAP_TILE_OPACITY },
    // The map's credits: always shown, small and quiet, and they wrap on a phone (Benoit,
    // 2026-10-02, nobgp/app#449). ⚠ Two classes in the selector, to win over the library's own
    // stylesheet, which loads after this one.
    '.maplibregl-ctrl.maplibregl-ctrl-attrib': {
      margin: 0,
      padding: '1px 6px',
      fontSize: 10,
      lineHeight: 1.5,
      color: palette.text.secondary,
      backgroundColor: palette.transparent.lighten,
      borderTopLeftRadius: 6,
    },
    '.maplibregl-ctrl.maplibregl-ctrl-attrib a': { color: 'inherit' },
    // The + and − buttons, in the colours of the page: the library draws them white in each theme.
    '.maplibregl-ctrl.maplibregl-ctrl-group': {
      backgroundColor: palette.background.paper,
      boxShadow: `0 0 0 1px ${palette.divider}`,
    },
    '.maplibregl-ctrl-group button + button': { borderTopColor: palette.divider },
    ...(palette.mode === 'dark' ? { '.maplibregl-ctrl-group .maplibregl-ctrl-icon': { filter: 'invert(1)' } } : {}),
    // A location on the map is a data center icon (`drawDataCenter` in `mapCore.ts`): a box with
    // three racks. Small, in the middle of its ring of nodes; full size, with the count of its
    // nodes, when it is too crowded to draw them (`layoutView` in `mapPlaces.ts`).
    '.map .point .datacenter .box': {
      fill: palette.background.paper,
      stroke: palette.contrast.medium,
      strokeWidth: 1.5,
      transition: 'stroke 0.4s',
    },
    '.map .point .datacenter .racks rect': { fill: 'none', stroke: palette.contrast.high, strokeWidth: 1.4 },
    // The lights are the noBGP orange (Benoit, 2026-10-02, nobgp/app#455: "make the lights orange").
    '.map .point .datacenter .racks circle': { fill: palette.secondary.main },
    // The count on an icon: the nodes it stands for, in the colour of a node (`drawCount`). Also on
    // the user icon, which is the icon of its own place.
    // ⚠ Every property is set here, stroke and weight too: a state of the icon (lit, selected,
    // offline) sets them on the whole group, and the count drew bold when the icon was lit.
    '.map .point .count rect': { fill: palette.node.main, stroke: palette.background.paper, strokeWidth: 1.5 },
    '.map .point .count text': {
      fill: palette.background.paper,
      stroke: 'none',
      strokeWidth: 0,
      fontSize: 10.5,
      fontWeight: 600,
      fontFamily: typography.fontFamily,
      pointerEvents: 'none',
    },
    '.map .point .count': { opacity: 1 },
    // The viewer's place (`drawHome`): the data center icon with
    // a small house in the noBGP orange.
    // ⚠ Every property is set here, as on the count: a state of the icon sets them on the group.
    '.map .point .datacenter.home .house .fill': { fill: palette.background.paper, stroke: 'none', strokeWidth: 0 },
    '.map .point .datacenter.home .house .edge': {
      fill: 'none',
      stroke: palette.background.paper,
      strokeWidth: 4.6,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    '.map .point .datacenter.home .house .line': {
      fill: 'none',
      stroke: palette.secondary.main,
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    '.map .point.offline .datacenter': { opacity: 0.55 },
    // A cloud location (`drawCloudPlace` in `marks.ts`), on the graph and on the map: the cloud's
    // icon in a box with the data center's edge and its states. ⚠ Not under `.map`: the graph draws
    // it too. And every property is set on the box: a state of the point sets them on the group.
    // ⚠ No frame at rest (Benoit, 2026-10-21), so the icon fills the box. The box stays: it is the
    // hit area, and a lit, selected or streaming cloud draws its edge.
    '.point .cloud-place .box': {
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 1.5,
      transition: 'stroke 0.4s',
    },
    '.point.offline .cloud-place': { opacity: 0.55 },
    // ⚠ The cloud keeps no outline: a selected or lit point sets a stroke on the whole group, and the
    // orange cloud took it (the Grok icon is an image, and takes none).
    '.point .cloud-place svg path': { stroke: 'none' },
    '.point.streaming .cloud-place .box': { stroke: palette.stream.main, strokeWidth: 2 },
    '.point.active .cloud-place .box, .point.selected .cloud-place .box': { stroke: palette.primary.main, strokeWidth: 2.5 },
    // Its name: the map names no cloud, so it is the one location name on the map.
    '.map .label.location': { fill: palette.contrast.high, fontWeight: 600 },
    '.map .point.streaming .datacenter .box': { stroke: palette.stream.main, strokeWidth: 2 },
    '.map .point.active .datacenter .box, .map .point.selected .datacenter .box': {
      stroke: palette.primary.main,
      strokeWidth: 2.5,
    },
    // A node on an inner ring: the outer ring is where its name would go, so the name shows only
    // when the node is lit or selected.
    '.map .label.inner:not(.active):not(.selected)': { opacity: 0 },
    '.map .point .marks.inner:not(.active):not(.selected)': { opacity: 0 },
    // An offline node's name is shown in the quiet colour on the map, so its marks are too.
    '.map .point .marks.offline': { opacity: 0.6 },
    // On the selected node's pill (filled in the node colour) the marks take the name's colour,
    // and the orange cloud an outline in it, or it is lost on the cyan.
    '.map .point .marks.selected .aws path, .map .point .marks.selected .tailscale path': {
      fill: palette.background.paper,
    },
    '.map .point .marks.selected .nobgp path': { stroke: palette.background.paper, strokeWidth: 2 },
    // An offline node is the pale fill of the graph, which is lost on the tiles. On the map it has
    // an outline in the colour of a node, so it is seen and still reads as off (Benoit, 2026-10-02).
    '.map .node.point.offline': { stroke: palette.node.main, strokeWidth: 2 },
    // A node with no place of its own, drawn at the viewer's place: a dashed ring says so.
    '.map .point .viewer-ring': {
      fill: 'none',
      stroke: palette.contrast.medium,
      strokeWidth: 1.5,
      strokeDasharray: '3,3',
      pointerEvents: 'none',
    },
    '.label': {
      fill: palette.text.secondary,
      fontSize: typography.fontSize,
      pointerEvents: 'none',
      transition: 'opacity 0.4s, fill 0.4s',
    },
    '.label > .sub': { fontSize: typography.caption.fontSize, fill: palette.contrast.low },
    '.label.selected': { fill: palette.contrast.max, fontWeight: 600 },
    // An offline node keeps its name, in grey, so the reader can see which node it is.
    '.label.offline': { opacity: 1, fill: palette.text.disabled },
    '.label.node.hidden': { opacity: 1, fill: palette.contrast.low },
    '.label.active': {
      opacity: 1,
      fill: palette.contrast.max,
      fontWeight: 500,
      transition: 'opacity 0.12s, fill 0.12s',
    },
    '.label.location': { fill: palette.contrast.low },
    '.label.service': { fontSize: typography.caption.fontSize, pointerEvents: 'none' },
    '.label tspan.match': { fill: palette.contrast.max, fontWeight: 500 },
    '.hidden': { opacity: 0, transition: 'opacity 0.4s' },
    '.hidden.offline': { opacity: 0 },
    '.hidden.active': { opacity: 1 },
    // ⚠ ONE `.point` RULE, and it carries the transition: these are the keys of one object, so a
    // second `.point` entry does not cascade — it REPLACES this one. The transition lives on the
    // `g` and the shape inside inherits the value mid-flight, which is what carries the fade
    // through `renderer.node`'s re-render of a stale node.
    '.point': { cursor: 'pointer', transition: 'stroke 0.7s ease-out, stroke-width 0.7s ease-out' },
    '.connected': { strokeDasharray: 'none' },
    '.outline': { fill: 'none', stroke: palette.contrast.max, strokeWidth: 7 },
    // ⚠ Every property is set on the arc itself: a state of the point sets them on the group. The
    // arc is decoration: the node's hover and click are unchanged.
    '.point .arcs': { pointerEvents: 'none', fill: 'none' },
    '.point .arcs circle, .point .arcs path': { fill: 'none', strokeWidth: 3, strokeDasharray: 'none' },
    // The hub of the nodes in no network: quiet, because it is no network.
    '.network.point.no-network': { fill: palette.contrast.low },
    ...hueRules(palette.mode),
  }
}
